import {
  ListItemIcon,
  ListItemText,
  MenuItem,
  Typography,
} from '@mui/material';
import { Circle } from '@mui/icons-material';
import { isValidElement, ReactNode } from 'react';
import { TFunction } from 'i18next';

import {
  AQI_LEVELS,
  AQI_LEVELS_COLORS,
} from 'features/aqiSensors/aqiSensors.constants';
import { Yup } from 'shared/yup-extended';

export const getMenuOptions = (t: TFunction): ReactNode[] => {
  return [
    <MenuItem key="50" value="50">
      <ListItemIcon>
        <Circle htmlColor={AQI_LEVELS_COLORS[AQI_LEVELS.moderate]} />
      </ListItemIcon>
      <ListItemText sx={{ overflow: 'hidden' }}>
        {t('aqiSensors.alerts.inputs.aqiSelection.options.50')}
      </ListItemText>
    </MenuItem>,
    <MenuItem key="100" value="100">
      <ListItemIcon>
        <Circle htmlColor={AQI_LEVELS_COLORS[AQI_LEVELS.unhealthySensitive]} />
      </ListItemIcon>
      <ListItemText sx={{ overflow: 'hidden' }}>
        {t('aqiSensors.alerts.inputs.aqiSelection.options.100')}
      </ListItemText>
    </MenuItem>,
    <MenuItem key="150" value="150">
      <ListItemIcon>
        <Circle htmlColor={AQI_LEVELS_COLORS[AQI_LEVELS.unhealthy]} />
      </ListItemIcon>
      <ListItemText sx={{ overflow: 'hidden' }}>
        {t('aqiSensors.alerts.inputs.aqiSelection.options.150')}
      </ListItemText>
    </MenuItem>,
    <MenuItem key="200" value="200">
      <ListItemIcon>
        <Circle htmlColor={AQI_LEVELS_COLORS[AQI_LEVELS.veryUnhealthy]} />
      </ListItemIcon>
      <ListItemText sx={{ overflow: 'hidden' }}>
        {t('aqiSensors.alerts.inputs.aqiSelection.options.200')}
      </ListItemText>
    </MenuItem>,
    <MenuItem key="300" value="300">
      <ListItemIcon>
        <Circle htmlColor={AQI_LEVELS_COLORS[AQI_LEVELS.hazardous]} />
      </ListItemIcon>
      <ListItemText sx={{ overflow: 'hidden' }}>
        {t('aqiSensors.alerts.inputs.aqiSelection.options.300')}
      </ListItemText>
    </MenuItem>,
    <MenuItem key="custom" value="custom">
      <ListItemText sx={{ overflow: 'hidden' }}>
        {t('aqiSensors.alerts.inputs.aqiSelection.options.custom')}
      </ListItemText>
    </MenuItem>,
  ];
};

// MUI mangles the display of the selections, so need to resort to some stuff here
export const getSelectedMenuOption = (
  value: string,
  t: TFunction,
): ReactNode | null => {
  // For some reason, we show different text if they select the custom option
  if (value === 'custom') {
    return (
      <Typography>
        {t('aqiSensors.alerts.inputs.aqiSelection.options.customShort')}
      </Typography>
    );
  }

  const option = getMenuOptions(t).find((opt: ReactNode) => {
    return isValidElement(opt) && opt.props.value === value;
  });

  return option ?? null;
};

export const AQI_OPTIONS = [
  '',
  '50',
  '100',
  '150',
  '200',
  '300',
  'custom',
] as const;

export const getValidationSchema = (t: TFunction): Yup.SchemaOf<object> => {
  return Yup.object().shape({
    aqiSelection: Yup.string()
      .required(t('aqiSensors.alerts.inputs.aqiSelection.required'))
      .oneOf([...AQI_OPTIONS], 'aqiSensors.alerts.inputs.aqiSelection.invalid'),
    thresholdValue: Yup.number().when('aqiSelection', (aqiSelection) => {
      if (aqiSelection !== 'custom') return Yup.string();

      return Yup.number()
        .typeError(t('aqiSensors.alerts.inputs.thresholdValue.invalid'))
        .required(t('aqiSensors.alerts.inputs.thresholdValue.required'))
        .min(0.1, t('aqiSensors.alerts.inputs.thresholdValue.min'))
        .max(500, t('aqiSensors.alerts.inputs.thresholdValue.max'));
    }),
  });
};
